import { Component, OnInit, EventEmitter, Input, Output, DestroyRef, input } from '@angular/core';
import * as _ from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { ISelectOption } from 'src/app/models/selectOptions';
import { ISelectOptionGroups } from 'src/app/models/selectOptions/selectOptionGroups.interface';

import { AiFindingsHttpService } from 'src/app/services/httpServices/detection/aiFindingsHttpService/ai-findings-http.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AiDataInsightsFiltersModel } from 'src/app/models/filters/implementations/pageFilters/aiDataInsightsFilters.model';

@Component({
  selector: 'app-ai-data-insights-vertical-filters',
  templateUrl: './ai-data-insights-vertical-filters.component.html',
  styleUrls: ['./ai-data-insights-vertical-filters.component.scss'],
})
export class AiDataInsightsVerticalFiltersComponent implements OnInit {
  @Input() public withCategories = true;
  @Input() public isFiltersMenuOpen: boolean;
  @Output() public filtersOnMenuStateChange = new EventEmitter<boolean>();

  @Output() public optionOnSelection = new EventEmitter<ISelectOption[]>();

  public pageFilters = input.required<AiDataInsightsFiltersModel>();
  public optionsInsideCategories$: BehaviorSubject<ISelectOptionGroups[]> = new BehaviorSubject<ISelectOptionGroups[]>(
    []
  );
  public expandedCategories: { [optionGroup: string]: boolean } = {};
  public selectedIds: Set<string>;
  public isOpen = true;

  public selectedOptions: ISelectOption[] = [];

  public dynamicCategories: string[];

  constructor(
    private readonly _aiFindingsHttpService: AiFindingsHttpService,
    private readonly _destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.dynamicCategories = this.pageFilters().getDynamicFilters();

    this.pageFilters()
      .getAllFiltersOptions()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((options) => {
        const optionsInsideCategories: ISelectOptionGroups[] = this._handleOptionsInsideCategories(options);
        this.dynamicCategories.forEach((category) => {
          optionsInsideCategories.push({
            displayName: category,
            options: [],
            isDynamic: true,
            isNew: false,
            selectedCount: 0,
          });
        });

        this.optionsInsideCategories$.next(optionsInsideCategories);
      });

    this.pageFilters()
      .onUpdate$.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        const newSelectedIds: Set<string> = new Set();

        this.pageFilters()
          .getAllSelectedOptions()
          .forEach((option) => {
            newSelectedIds.add(option.id);
          });
        this.selectedIds = newSelectedIds;
      });
  }

  private _handleOptionsInsideCategories(options: ISelectOption[]): ISelectOptionGroups[] {
    const optionsInsideCategories: ISelectOptionGroups[] = [];
    const indexByCategory: { [key: string]: number } = {};
    options.forEach((option) => {
      if (option.categoryName) {
        if (option.categoryName in indexByCategory) {
          optionsInsideCategories[indexByCategory[option.categoryName]].options.push(option);
        } else if (!this.dynamicCategories.includes(option.categoryName)) {
          optionsInsideCategories.push({
            displayName: option.categoryName,
            options: [option],
            isDynamic: false,
            isNew: false,
            selectedCount: 0,
          });
          indexByCategory[option.categoryName] = optionsInsideCategories.length - 1;
        } else {
          optionsInsideCategories.push({
            displayName: option.categoryName,
            options: [option],
            isDynamic: true,
            isNew: false,
            selectedCount: 0,
          });
        }
      } else {
        optionsInsideCategories.push({
          options: [option],
        } as ISelectOptionGroups);
      }
    });
    return optionsInsideCategories;
  }

  public expandClicked(optionGroupDisplayName: string, status: boolean) {
    this.expandedCategories[optionGroupDisplayName] = status;
  }

  public onToggleClick() {
    this.filtersOnMenuStateChange.emit(!this.isFiltersMenuOpen);
    this.isOpen = !this.isOpen;
  }

  public tablesFiltersChange(selectedOptions: ISelectOption[]) {
    this.pageFilters().updateFilterFromUserSelection(undefined, selectedOptions);
  }

  public onSelect(id: string): void {
    if (!this.selectedIds.has(id)) {
      this.selectedIds.add(id);
    } else {
      this.selectedIds.delete(id);
    }
    this.optionOnSelection.emit(this._getSelectedOptions());
  }

  private _getSelectedOptions(): ISelectOption[] {
    const selectedIndexArray = Array.from(this.selectedIds);
    const optionsById = _.keyBy(this.pageFilters().getAllFiltersOptions().value, 'id');

    /// This is needed because there are options that can be selected but are not shown.
    const selectedOptions = selectedIndexArray.map((id: string) => optionsById[id]);

    return selectedOptions.filter((options) => typeof options !== 'undefined');
  }
}
